/**
 * Shared guards for customer-facing narrative fields — blocks
 * instruction-shaped LLM text from render, export, and card surfaces.
 */
import { detectInstructionShape } from './instructionShapeGuard';
import type { SiteRagReport } from '../types/siteRag';

/** Coerce legacy / LLM-shaped narrative fields to plain string prose. */
export function coerceNarrativeText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.summary === 'string') return obj.summary;
    if (typeof obj.text === 'string') return obj.text;
    if (typeof obj.narrative === 'string') return obj.narrative;
  }
  return '';
}

/** True when text is non-empty and not instruction-shaped. */
export function isRenderableNarrative(text: string | undefined | null): boolean {
  const trimmed = text?.trim();
  if (!trimmed) return false;
  return !detectInstructionShape(trimmed).flagged;
}

/** Returns trimmed prose or empty string when missing or instruction-shaped. */
export function safeNarrativeProse(text: string | undefined | null): string {
  const trimmed = text?.trim();
  if (!trimmed || detectInstructionShape(trimmed).flagged) return '';
  return trimmed;
}

/** Executive summary safe to append as an intent-overview snippet. */
export function safeExecSummarySnippet(
  report: Pick<SiteRagReport, 'executiveSummary' | 'sectionGuardStatus'>,
): string | undefined {
  if (report.sectionGuardStatus?.executiveSummary === 'degraded-instruction-leak') {
    return undefined;
  }
  const exec = coerceNarrativeText(report.executiveSummary).trim();
  if (!exec || !isRenderableNarrative(exec)) return undefined;
  return exec;
}

/** Drop instruction-shaped paragraphs from multi-paragraph overview prose. */
export function filterRenderableParagraphs(text: string): string {
  const kept = text
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0 && isRenderableNarrative(p));
  return kept.join('\n\n');
}

/**
 * Redhead review wave 3 (defect 7) — app ONBOARDING copy must never render
 * inside report body prose. The "Set Project Intent in site configuration
 * (capacity, cooling, …) to tailor scoring…" call-to-action is in-app
 * guidance (site-configuration UI), not report content, but legacy generated
 * reports persisted it into `projectIntentOverview` (and the editorial
 * narrative could re-voice it verbatim). Strip the CTA sentence wherever
 * report prose is resolved for display/export; paragraphs emptied by the
 * removal are dropped.
 *
 * Scoped to the exact CTA shape ("Set Project Intent …" through the end of
 * its sentence) so real narrative prose is never touched.
 */
const APP_ONBOARDING_SENTENCE_RE = /(?:^|(?<=\s))Set (?:the )?Project Intent\b[^.!?\n]*[.!?]?[ \t]*/g;

export function stripAppOnboardingCopy(text: string): string {
  if (!text || !/Set (?:the )?Project Intent\b/.test(text)) return text ?? '';
  const out = text
    .replace(APP_ONBOARDING_SENTENCE_RE, '')
    .replace(/[ \t]{2,}/g, ' ');
  const cleaned = out
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0)
    .join('\n\n')
    .trim();
  console.warn('[safeNarrativeProse] removed app onboarding copy ("Set Project Intent…") from report prose');
  return cleaned;
}
